import { Routes } from '@angular/router';
import { authGuard, publicGuard, mfaGuard } from './core/guards/auth.guard';
import { beaconAdminGuard } from './features/admin/admin-access.guard';
// FEATURE FLAGS DISABLED — guards commented out in feature-flag.guards.ts
// import { calcV2Guard, disclosureGuard, supervisorGuard, marketStudyGuard } from './core/guards/feature-flag.guards';
import { moduleGuard, anyModuleGuard } from './core/guards/module-permission.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { RatesRailComponent } from './features/rates/rail/rates-rail.component';
import { ChangesRailComponent } from './features/changes/changes-rail.component';
import { CalculatorRailComponent } from './features/calculator/rail/calculator-rail.component';
import { ProductSearchRailComponent } from './features/products/rail/product-search-rail.component';
import { MygaRateSheetRailComponent } from './features/rate-sheet/rail/myga-rate-sheet-rail.component';
import { IaRateSheetRailComponent } from './features/ia-rate-sheet/rail/ia-rate-sheet-rail.component';
import { RilaRateSheetRailComponent } from './features/rila-rate-sheet/rail/rila-rate-sheet-rail.component';
import { CalculatorV3RailComponent } from './features/calculator-v3/rail/calculator-v3-rail.component';

export const routes: Routes = [
  // ── Public auth routes ──────────────────────────────────────
  {
    path: 'login',
    canActivate: [publicGuard],
    loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent),
    title: 'Sign In'
  },
  {
    path: 'auth',
    children: [
      {
        path: 'mfa-select',
        canActivate: [mfaGuard],
        loadComponent: () => import('./features/auth/mfa-select/mfa-select.component').then(m => m.MfaSelectComponent),
        title: 'Verify Identity'
      },
      {
        path: 'mfa-verify',
        canActivate: [mfaGuard],
        loadComponent: () => import('./features/auth/mfa-verify/mfa-verify.component').then(m => m.MfaVerifyComponent),
        title: 'Enter Code'
      },
      {
        path: 'verify-link',
        loadComponent: () => import('./features/auth/verify-link/verify-link.component').then(m => m.VerifyLinkComponent),
        title: 'Signing In'
      },
      {
        path: 'login',
        loadComponent: () => import('./features/auth/token-login/token-login.component').then(m => m.TokenLoginComponent),
        title: 'Signing In'
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./features/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
        title: 'Reset Password'
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./features/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
        title: 'Set New Password'
      },
    ]
  },

  // ── Headless / renderer routes (no auth — accessed by Playwright PDF server) ──
  {
    path: 'disclosures/report',
    loadComponent: () => import('./features/disclosures/disclosure-report/disclosure-report.component').then(m => m.DisclosureReportComponent),
    title: 'Disclosure Report',
  },

  // ── Protected routes (require authentication) ───────────────
  {
    path: '',
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'rates', pathMatch: 'full' },
      { path: 'dashboard', loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent), title: 'Dashboard' },
      { path: 'rates',
        // canActivate: [moduleGuard('full_rates_grid')],
        loadComponent: () => import('./features/rates/rates.component').then(m => m.RatesComponent),
        title: 'Rates',
        data: { rail: RatesRailComponent } },
      {
        // Consolidated forward view of rate changes across all four product
        // types. /rates holds row data PER ProductType, so it structurally
        // cannot say "eleven changes land Thursday, across six carriers,
        // spanning FA and IA and Structured" — a cross-type day only exists
        // here. Reads the three delta views through GET /api/v1/rate_changes,
        // so it needs nothing from the shared rate views and cannot regress
        // them.
        //
        // Ungated for now. If a module gate is added it has to land in THREE
        // places — here, nav-v3 and the legacy sidebar — or the nav advertises
        // a destination the router bounces.
        path: 'changes',
        loadComponent: () => import('./features/changes/upcoming-changes.component')
          .then(m => m.UpcomingChangesComponent),
        title: 'Upcoming Rate Changes',
        data: { rail: ChangesRailComponent } },
      {
        path: 'calculator',
        // canActivate: [moduleGuard('benefit_calculator')],
        loadComponent: () => import('./features/calculator/calculator.component').then(m => m.CalculatorComponent),
        title: 'Benefit Calculator',
        data: { rail: CalculatorRailComponent }
      },
      {
        path: 'calculator-v2',
        // canActivate: [calcV2Guard, moduleGuard('calc_v2')],
        loadComponent: () => import('./features/calculator-v2/calculator-v2.component').then(m => m.CalculatorV2Component),
        loadChildren: () => import('./features/calculator-v2/calculator-v2.routes').then(m => m.calculatorV2Routes),
        title: 'Benefit Calculator V2'
      },
      {
        path: 'calculator-v3',
        loadComponent: () => import('./features/calculator-v3/calculator-v3.component').then(m => m.CalculatorV3Component),
        title: 'Calculator v3 (Preview)',
        data: { rail: CalculatorV3RailComponent }
      },
      {
        path: 'disclosures',
        // canActivate: [disclosureGuard],
        loadComponent: () => import('./features/disclosures/disclosures.component').then(m => m.DisclosuresComponent),
        title: 'Disclosures',
      },
      {
        path: 'disclosures/supervisor',
        // canActivate: [supervisorGuard],
        loadComponent: () => import('./features/disclosures/supervisor/supervisor-queue.component').then(m => m.SupervisorQueueComponent),
        title: 'Supervisor Queue',
        data: { preserveRail: true },
      },
      { path: 'sales',
        // canActivate: [marketStudyGuard],
        loadComponent: () => import('./features/sales/sales.component').then(m => m.SalesComponent),
        title: 'Sales' },
      { path: 'inbox', loadComponent: () => import('./features/inbox/inbox.component').then(m => m.InboxComponent), title: 'Inbox' },
      { path: 'rate-sheet',
        // canActivate: [moduleGuard('fa_broker_rate_sheet')],
        loadComponent: () => import('./features/rate-sheet/rate-sheet.component').then(m => m.RateSheetComponent),
        title: 'MYGA Rate Sheet',
        data: { rail: MygaRateSheetRailComponent, preserveRail: true } },
      { path: 'standard-fixed-rate-sheet',
        // canActivate: [moduleGuard('fa_broker_rate_sheet')],
        loadComponent: () => import('./features/rate-sheet/rate-sheet.component').then(m => m.RateSheetComponent),
        title: 'Standard Fixed Rate Sheet',
        data: { rail: MygaRateSheetRailComponent, preserveRail: true } },
      { path: 'ia-rate-sheet',
        // canActivate: [moduleGuard('index_broker_rate_sheet')],
        loadComponent: () => import('./features/ia-rate-sheet/ia-rate-sheet.component').then(m => m.IaRateSheetComponent),
        title: 'Index Rate Sheet',
        data: { rail: IaRateSheetRailComponent, preserveRail: true } },
      { path: 'rila-rate-sheet',
        // canActivate: [moduleGuard('index_var_broker_rate_sheet')],
        loadComponent: () => import('./features/rila-rate-sheet/rila-rate-sheet.component').then(m => m.RilaRateSheetComponent),
        title: 'RILA Rate Sheet',
        data: { rail: RilaRateSheetRailComponent, preserveRail: true } },
      { path: 'clients/jp-morgan', loadComponent: () => import('./features/jpm-minisite/jpm-minisite.component').then(m => m.JpmMinisiteComponent), title: 'J.P. Morgan' },
      { path: 'search', redirectTo: 'rates', pathMatch: 'full' },
      { path: 'products',
        // canActivate: [anyModuleGuard(['fa_product_search', 'index_product_search', 'index_var_product_search'])],
        loadComponent: () => import('./features/products/products.component').then(m => m.ProductsComponent),
        title: 'Product Search',
        data: { rail: ProductSearchRailComponent } },
      {
        path: 'broker/cases',
        // canActivate: [moduleGuard('cases')],
        loadComponent: () =>
          import('./features/broker/cases/cases-list.component')
            .then(m => m.CasesListComponent),
        title: 'Case Scenarios',
      },
      {
        path: 'broker/cases/:id',
        // canActivate: [moduleGuard('cases')],
        loadComponent: () =>
          import('./features/broker/cases/case-detail.component')
            .then(m => m.CaseDetailComponent),
        title: 'Case Scenario',
      },
      { path: 'compare', redirectTo: '/calculator/compare/income', pathMatch: 'full' },
      {
        path: 'compare-legacy',
        // canActivate: [moduleGuard('compare_legacy')],
        loadComponent: () => import('./sbs/containers/sbs-router/sbs-router.component')
          .then(m => m.SbsRouterComponent),
        title: 'Product Comparison (Legacy)'
      },
      {
        path: 'calculator/compare',
        loadComponent: () => import('./shared/comparison/allocation/containers/calc-allocation-page/calc-allocation-page.component')
          .then(m => m.CalcAllocationPageComponent),
        title: 'Income Comparison'
      },
      {
        path: 'calculator/compare/income',
        loadComponent: () => import('./shared/comparison/containers/income-compare-page/income-compare-page.component')
          .then(m => m.IncomeComparePageComponent),
        title: 'Income Projection'
      },
      {
        path: 'compare/structural',
        loadComponent: () => import('./features/structural-compare/structural-compare.component')
          .then(m => m.StructuralCompareComponent),
        title: 'Compare'
      },
      { path: 'documents', redirectTo: 'rates', pathMatch: 'full' },
      {
        path: 'data-dictionary',
        loadComponent: () => import('./features/data-dictionary/data-dictionary.component').then(m => m.DataDictionaryComponent),
        title: 'Data Dictionary',
      },
      {
        path: 'account',
        loadComponent: () => import('./features/account/account-shell.component').then(m => m.AccountShellComponent),
        title: 'Account',
        children: [
          { path: '', redirectTo: 'settings', pathMatch: 'full' },
          { path: 'settings', canDeactivate: [unsavedChangesGuard], loadComponent: () => import('./features/account/account-settings.component').then(m => m.AccountSettingsComponent), title: 'Account Settings' },
          { path: 'defaults', loadComponent: () => import('./features/account/account-defaults.component').then(m => m.AccountDefaultsComponent), title: 'Defaults for new cases' },
          { path: 'shelves',  loadComponent: () => import('./features/account/account-shelves.component').then(m => m.AccountShelvesComponent), title: 'Custom Shelves' },
          { path: 'security', loadComponent: () => import('./features/account/account-security.component').then(m => m.AccountSecurityComponent), title: 'Security' },
        ],
      },
      {
        path: 'my-sheets/:db/:ssId',
        loadComponent: () => import('./features/rates/sheet-redirect.component').then(m => m.SheetRedirectComponent),
        title: 'My Rate Sheet'
      },
      {
        path: 'admin',
        canActivate: [beaconAdminGuard],
        loadComponent: () => import('./features/admin/admin-shell.component').then(m => m.AdminShellComponent),
        loadChildren: () => import('./features/admin/admin.routes').then(m => m.adminRoutes),
        title: 'Admin'
      },
      { path: 'commissions', redirectTo: 'admin/commissions', pathMatch: 'full' },
      { path: 'commissions/new', redirectTo: 'admin/commissions/new', pathMatch: 'full' },
      { path: 'commissions/:id', redirectTo: 'admin/commissions/:id', pathMatch: 'full' },
      { path: 'announcements', redirectTo: 'admin/announcements', pathMatch: 'full' },
      { path: 'announcements/new', redirectTo: 'admin/announcements/new', pathMatch: 'full' },
      { path: 'announcements/:id', redirectTo: 'admin/announcements/:id', pathMatch: 'full' },
      { path: 'design-system', loadComponent: () => import('./features/design-system/design-system.component').then(m => m.DesignSystemComponent), title: 'Design System' },
      { path: 'design-system/rider-card', loadComponent: () => import('./features/design-system/rider-card-demo/rider-card-demo.component').then(m => m.RiderCardDemoComponent), title: 'Rider Card Demo' },
      { path: '**', redirectTo: 'dashboard' },
    ]
  },
];
